import { Timestamp } from 'firebase/firestore'
import { createAiConversationWithMessages } from '@/firebase/ai-conversations'
import { generateAiAssistantSystemMessage } from '@/utils/ai-assistant'
import { DASHBOARD_CHAT_ORIGIN } from '@/utils/axi-chat-origin'
import { AiConversationMessageRole } from '@/types/firebase/ai-conversation'
import type {
  IAiConversationOrigin,
  IAiConversationBase,
  IAiConversationMessageSystem,
} from '@/types/firebase/ai-conversation'

export const clearCarriedHistory = () => {
  if (typeof window !== 'undefined' && window.location.hash.startsWith('#h=')) {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`,
    )
  }
}

// Keep whole recent turns within the decoder limit, rather than silently
// dropping a long conversation at sign-in. Preserve an image only on its turn.
// With a nonce (one per /ask-axi visit) the payload is {n, t}, so the same
// question asked again later starts its own conversation; the WordPress
// templates still send the plain-array form, which decodes without a nonce.
export function encodeCarriedHistory(
  turns: ICarriedChatTurn[],
  nonce?: string,
): string {
  const recent = turns.slice(-6).map((turn) => ({
    role: turn.role,
    content: turn.content.slice(0, 4000),
    ...(safeCarriedQuestionImage(turn.imageUrl)
      ? { imageUrl: safeCarriedQuestionImage(turn.imageUrl) }
      : {}),
  }))
  while (recent.length) {
    const bytes = new TextEncoder().encode(
      JSON.stringify(nonce ? { n: nonce, t: recent } : recent),
    )
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    const payload = btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
    if (payload.length <= 24000) return `#h=${payload}`
    if (recent.length === 1)
      recent[0].content = recent[0].content.slice(
        0,
        Math.floor(recent[0].content.length / 2),
      )
    else recent.shift()
  }
  return ''
}

export interface ICarriedChatTurn {
  role: 'user' | 'assistant'
  content: string
  imageUrl?: string
}

// Decoded turns, plus the handoff nonce when the payload carried one.
export type ICarriedHistory = ICarriedChatTurn[] & { nonce?: string }

export const newCarriedNonce = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

// One automatic Axi reply per carried handoff, decided once, on the first
// load of the carried conversation that has its messages: reply only when
// the carried chat ends with an unanswered student turn. Later student
// messages are answered by the send path, never by this check.
export function carriedReplyDecision({
  carriedConversationId,
  conversationId,
  checked,
  messageRoles,
}: {
  carriedConversationId: string | null
  conversationId: string
  checked: boolean
  messageRoles: string[]
}): { checked: boolean; reply: boolean } {
  if (
    checked ||
    !carriedConversationId ||
    conversationId !== carriedConversationId ||
    !messageRoles.length
  ) {
    return { checked, reply: false }
  }
  return { checked: true, reply: messageRoles[messageRoles.length - 1] === 'user' }
}

// Live WP question-image producers: single-exampapers.php and
// single-examquestions.php. Restrict image data to their audited static hosts.
export const safeCarriedQuestionImage = (
  value: unknown,
): string | undefined => {
  if (typeof value !== 'string' || value.length > 2048) return undefined
  try {
    const url = new URL(value)
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      url.port ||
      url.search ||
      url.hash
    )
      return undefined
    const wp =
      url.hostname === 'www.examsolutions.net' &&
      url.pathname.startsWith('/wp-content/uploads/')
    const s3 =
      url.hostname === 'examsolutions.s3.eu-west-2.amazonaws.com' &&
      url.pathname.startsWith('/Work+Solution/')
    if (!(wp || s3) || !/\.(png|jpe?g)$/i.test(url.pathname)) return undefined
    return url.href
  } catch {
    return undefined
  }
}

// Read and sanitize #h without destroying valid history. Invalid payloads
// are cleared immediately; valid payloads are cleared only after import.
// Images stay on the original first-user turn, never moved to a later one.
export const decodeCarriedHistory = (): ICarriedHistory | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  const match = window.location.hash.match(/^#h=(.+)$/)

  if (!match) {
    return undefined
  }

  // Keep a valid payload until the complete atomic import succeeds. Reloads
  // and transient save failures must not discard the only copy.
  const discardInvalid = () => clearCarriedHistory()

  try {
    if (match[1].length > 24000) {
      discardInvalid()
      return undefined
    }
    const b64 = match[1].replace(/-/g, '+').replace(/_/g, '/')
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    const raw = JSON.parse(new TextDecoder().decode(bytes))
    const wrapped = !!raw && !Array.isArray(raw) && Array.isArray(raw.t)
    const parsed = wrapped ? raw.t : raw
    const nonce =
      wrapped && typeof raw.n === 'string' && raw.n.length <= 64
        ? raw.n
        : undefined

    if (!Array.isArray(parsed)) {
      discardInvalid()
      return undefined
    }

    const originalFirstUser = parsed.find(
      (turn) =>
        turn?.role === 'user' &&
        typeof turn.content === 'string' &&
        turn.content.trim(),
    )
    let userSeen = false
    const turns = parsed
      .filter(
        (t): t is ICarriedChatTurn =>
          !!t &&
          (t.role === 'user' || t.role === 'assistant') &&
          typeof t.content === 'string' &&
          !!t.content.trim(),
      )
      .slice(-6)
      .map((turn) => {
        const firstUser = turn.role === 'user' && !userSeen
        if (turn.role === 'user') userSeen = true
        const imageUrl =
          firstUser && turn === originalFirstUser
            ? safeCarriedQuestionImage(turn.imageUrl)
            : undefined
        return {
          role: turn.role,
          content: turn.content.slice(0, 4000),
          ...(imageUrl ? { imageUrl } : {}),
        }
      })

    if (!turns.length) {
      discardInvalid()
      return undefined
    }
    const history: ICarriedHistory = turns
    if (nonce) history.nonce = nonce
    return history
  } catch {
    discardInvalid()
    return undefined
  }
}

// Seeds the carried pre-login conversation as an Axi chat owned by the
// student. Returns the new conversation id. Shared by the assistant home
// (chat=1 handoff) and the topic/lesson paper handoff so both honor #h.
const createCarriedAxiConversation = async (
  userUid: string,
  carriedHistory: ICarriedChatTurn[],
  origin: IAiConversationOrigin = DASHBOARD_CHAT_ORIGIN,
  importId?: string,
): Promise<string> => {
  const systemMessage: IAiConversationMessageSystem = {
    id: '',
    content: { text: generateAiAssistantSystemMessage() },
    conversationId: '',
    role: AiConversationMessageRole.System,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  }

  const firstUserTurn = carriedHistory.find((t) => t.role === 'user')
  const titleSeed = firstUserTurn?.content.trim() ?? ''
  const createObj: IAiConversationBase = {
    userId: userUid,
    title: titleSeed
      ? titleSeed.length > 80
        ? `${titleSeed.slice(0, 77).trimEnd()}...`
        : titleSeed
      : 'Continued from ExamSolutions',
    systemMessage,
    // Started on the free site: back goes to an in-app place (dashboard
    // by default, or the paper the topic handoff landed on).
    origin,
  }

  return createAiConversationWithMessages(
    createObj,
    carriedHistory.map((turn) => ({
      role:
        turn.role === 'assistant'
          ? AiConversationMessageRole.Assistant
          : AiConversationMessageRole.User,
      content: {
        text: turn.content,
        plainText: true,
        ...(turn.role === 'user' && safeCarriedQuestionImage(turn.imageUrl)
          ? { imageUrls: [safeCarriedQuestionImage(turn.imageUrl)!] }
          : {}),
      },
      ...(turn.role === 'assistant' ? { reaction: null } : {}),
    })),
    importId,
  )
}

// The import id is derived from owner, sanitized turns, origin and the
// handoff nonce, so reopening the same handoff resumes its conversation
// (createAiConversationWithMessages finds it) and nothing is stored locally.
// Tabs of one browser take a Web Lock per import, so a second tab waits and
// then resumes instead of writing the same conversation twice.
const pendingImports = new Map<string, Promise<string>>()
export const seedCarriedAxiConversation = async (
  userUid: string,
  carriedHistory: ICarriedHistory,
  origin: IAiConversationOrigin = DASHBOARD_CHAT_ORIGIN,
): Promise<string> => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    const id = await createCarriedAxiConversation(
      userUid,
      carriedHistory,
      origin,
    )
    clearCarriedHistory()
    return id
  }
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(
      JSON.stringify({
        userUid,
        carriedHistory,
        origin,
        ...(carriedHistory.nonce ? { nonce: carriedHistory.nonce } : {}),
      }),
    ),
  )
  const key = `axi-carried-import:${Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')}`
  const pending = pendingImports.get(key)
  if (pending) return pending
  const runImport = async () => {
    const id = await createCarriedAxiConversation(
      userUid,
      carriedHistory,
      origin,
      key.replace('axi-carried-import:', 'carried-'),
    )
    clearCarriedHistory()
    return id
  }
  const locks =
    typeof navigator !== 'undefined'
      ? (navigator as { locks?: { request?: unknown } }).locks
      : undefined
  const operation =
    locks && typeof locks.request === 'function'
      ? (locks as {
          request: (name: string, fn: () => Promise<string>) => Promise<string>
        }).request(key, runImport)
      : runImport()
  pendingImports.set(key, operation)
  try {
    return await operation
  } finally {
    pendingImports.delete(key)
  }
}
